// https://github.com/angular/angular/issues/58522

import { ChangeDetectionStrategy, Component } from '@angular/core';
import { AgFilterComponent } from '../ag-filter.component';
import { IAfterGuiAttachedParams, IDoesFilterPassParams } from 'ag-grid-community';
import { MatChip, MatChipSet } from '@angular/material/chips';
import { getEnumValues } from '../../../../core/utilities';
import { MatDivider } from '@angular/material/divider';
import { MatError, MatFormField, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatDatepicker, MatDatepickerInput, MatDatepickerToggle } from '@angular/material/datepicker';
import { MatTimepicker, MatTimepickerInput, MatTimepickerToggle } from '@angular/material/timepicker';
import { GxInputDirective } from '../../../../directives/gxinput.directive';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from '@angular/forms';
import { takeUntil } from 'rxjs/operators';
import { BehaviorSubject } from 'rxjs';
import { LetDirective } from '@ngrx/component';
import { Interval, isWithinInterval, subDays } from 'date-fns';
import { formatDate } from '../../../../core/utils/date-fns-utils';
import { getErrorKey, QuickDateTimeSelection } from './date-time-utils';
import { TranslateModule } from '@ngx-translate/core';

@Component({
  selector: 'app-ag-date-time-filter',
  standalone: true,
  imports: [
    MatChipSet,
    MatChip,
    MatDivider,
    MatFormField,
    MatInput,
    MatDatepickerInput,
    MatDatepicker,
    MatDatepickerToggle,
    MatTimepickerInput,
    MatTimepicker,
    MatTimepickerToggle,
    GxInputDirective,
    FormsModule,
    ReactiveFormsModule,
    MatSuffix,
    MatError,
    LetDirective,
    TranslateModule,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './ag-date-time-filter.component.html',
  styleUrl: './ag-date-time-filter.component.scss',
})
export class AgDateTimeFilterComponent extends AgFilterComponent {

  protected now = new Date();

  protected readonly filterState = new FormGroup<{
    fromDate: FormControl<Date>;
    fromTime: FormControl<Date>;
    toDate: FormControl<Date>;
    toTime: FormControl<Date>;
  }>

  ({
    fromDate: new FormControl(null, { validators: Validators.required }),
    fromTime: new FormControl(new Date(null,
        null,
        null,
        0,
        0)),
    toDate: new FormControl(this.now, { validators: Validators.required }),
    toTime: new FormControl(new Date(null,
        null,
        null,
        this.now.getHours(),
        this.now.getMinutes())),
  });

  private readonly activeFilterModel = new BehaviorSubject<{
    from: string,
    to: string
  }>({ from: null, to: null });
  protected readonly activeFilterModel$ = this.activeFilterModel.asObservable();

  protected readonly quickDateTimeArr = getEnumValues(QuickDateTimeSelection);

  // use to dismiss filter popup
  private hidePopup: () => void;

  protected readonly getErrorKey = getErrorKey;

  constructor() {
    super();
    this.filterState.valueChanges
        .pipe(takeUntil(this.componentDestroyed))
        .subscribe(() => {
          this.now = new Date();
        });
  }


  // OVERRIDES


  override isFilterActive(): boolean {
    return this.filterState?.valid;
  }

  override getModel(): { from: string; to: string } {
    if (!this.isFilterActive()) {
      return null;
    }

    return {
      from: this.getDateTime('from')?.toISOString(),
      to: this.getDateTime('to')?.toISOString(),
    };
  }

  override setModel(model: { from: string, to: string }) {
    this.now = new Date();
    let fromDateTime = new Date(model?.from);
    let toDateTime = new Date(model?.to);

    const fromDateControl = this.filterState.get('fromDate');
    fromDateControl.setValue(!!model ? fromDateTime : null);

    const fromTimeControl = this.filterState.get('fromTime');
    fromTimeControl.setValue(!!model ? fromDateTime : new Date(null,
        null,
        null,
        0,
        0));

    const toDateControl = this.filterState.get('toDate');
    toDateControl.setValue(!!model ? toDateTime : new Date(this.now.getFullYear(),
        this.now.getMonth(),
        this.now.getDate()));

    const toTimeControl = this.filterState.get('toTime');
    toTimeControl.setValue(!!model ? toDateTime : new Date(null,
        null,
        null,
        this.now.getHours(),
        this.now.getMinutes()));

    this.filterState.markAllAsTouched({ emitEvent: false });

    // filterState.valid does not have the updated value without timeout for some reason
    setTimeout(() => {
      if (this.filterState.valid || !model) { // allow model = null to reset filter
        this.updateFilter();
      } else {
        console.warn(`Invalid filter value: ${JSON.stringify(model)}`);
      }
    });
  }

  override doesFilterPass(params: IDoesFilterPassParams): boolean {
    if (!this.isFilterActive()) {
      return true; // early return, filter is inactive
    }

    // get the value for this filter's column for the provided row data
    // https://www.ag-grid.com/angular-data-grid/component-floating-filter/
    // see Custom Filter And Custom Floating Filter for reference

    const { node } = params;
    const value = this.params.getValue(node);

    // check for callback in filter params, if not present, define default
    const filterCb: (filterOptionValue: {
      from: string,
      to: string
    }, cellValue: Date) => boolean = this.params?.filterCb ?
        this.params.filterCb :
        (filterValue: { from: string, to: string }, cellValue: Date): boolean => {
          let interval: Interval;

          interval = {
            start: new Date(filterValue.from),
            end: new Date(filterValue.to),
          };

          try {
            return isWithinInterval(cellValue, interval);
          } catch (e) {
            console.error(e);
            return false;
          }
        };

    // check for some falsy values (we want 0 and boolean false to pass to support number and
    // boolean filters)
    return value !== null &&
        value !== undefined &&
        value !== '' &&
        // run the filter
        filterCb(this.getModel(), value);
  }

  override updateFilter() {
    super.updateFilter();
    this.activeFilterModel.next(this.getModel());
  }


  // Specified in filter / floating filter  interface


  /**
   * Run logic immediately after filter popup is rendered.
   * @param params
   */
  afterGuiAttached(params: IAfterGuiAttachedParams) {
    this.hidePopup = params.hidePopup;
    this.filterState.markAllAsTouched({ emitEvent: false });
    this.now = new Date();
  }

  /**
   * Get model as string for floating filters
   * @param model
   */
  getModelAsString(model: { from: string, to: string }): string {
    const format = 'dd/MM/yyyy, HH:mm';
    return !model ?
        '' :
        formatDate(new Date(model.from), format) + ' - ' + formatDate(new Date(model.to), format);
  }


  // GETTERS, HANDLERS, HELPERS


  /**
   * Prevent the default closing on the filter popup upon date/time selection
   * https://www.ag-grid.com/javascript-data-grid/component-filter/#custom-filters-containing-a-popup-element
   * @private
   */
  preventDefault() {
    setTimeout(() => {
      const overlayContainer = document.getElementsByClassName('cdk-overlay-container')?.[0];
      overlayContainer?.classList?.add('ag-custom-component-popup');
    });
  }

  /**
   * Set back the default behaviour.
   * Useful when GRID is inside a popup. Without setting back the default, it won't be possible to 'click away'
   * an opened date/time filter.
   */
  setDefault() {
    setTimeout(() => {
      const overlayContainer = document.getElementsByClassName('cdk-overlay-container')?.[0];
      overlayContainer?.classList?.remove('ag-custom-component-popup');
    });
  }

  /**
   * Handle form reset.
   */
  onResetClick() {
    this.setModel(null);
  }

  /**
   * Set Model and dismiss filter popup.
   * @param selection
   */
  onQuickDateTimeClick(selection: QuickDateTimeSelection) {
    this.now = new Date();

    switch (selection) {
      case QuickDateTimeSelection['24H']:
        this.setModel({ from: subDays(this.now, 1).toISOString(), to: this.now.toISOString() });
        break;
      case QuickDateTimeSelection['72H']:
        this.setModel({ from: subDays(this.now, 3).toISOString(), to: this.now.toISOString() });
        break;
      case QuickDateTimeSelection['7D']:
        this.setModel({ from: subDays(this.now, 7).toISOString(), to: this.now.toISOString() });
        break;
      case QuickDateTimeSelection['28D']:
        this.setModel({ from: subDays(this.now, 28).toISOString(), to: this.now.toISOString() });
        break;
      default:
        return;
    }

    this.hidePopup();
  }

  /**
   * Limit max possible 'from' timepicker option
   */
  getFromTimeMax() {
    const fromDateTime = this.getDateTime('from');
    const toDateTime = this.getDateTime('to');

    // If date range is within the same day, 'from' time options < 'to' time options
    if (fromDateTime && toDateTime && fromDateTime.toDateString() === toDateTime.toDateString()) {
      return toDateTime.toISOString();
      // If 'from' date value is present day, show time options up to now
    } else if (fromDateTime && fromDateTime.toDateString() === this.now.toDateString()) {
      return this.now.toISOString();
    } else return undefined;
  }

  /**
   * Limit min possible 'to' timepicker option
   */
  getToTimeMin() {
    const fromDateTime = this.getDateTime('from');
    const toDateTime = this.getDateTime('to');

    // If date range is within the same day, 'to' time options > 'from' time options
    if (fromDateTime && toDateTime && fromDateTime.toDateString() === toDateTime.toDateString()) {
      return fromDateTime.toISOString();
    } else return undefined;
  }

  /**
   * Limit max possible 'to' timepicker option
   */
  getToTimeMax() {
    const toDateTime = this.getDateTime('to');

    // if 'to' date value is present day, allow time picker options up to now
    if (toDateTime && toDateTime.toDateString() === this.now.toDateString()) {
      return this.now.toISOString();
    } else return undefined;
  }

  /**
   * Limit min possible 'to' date
   */
  getToDateMin() {
    const fromDateTime = this.getDateTime('from');
    // if there is a 'from' date selected, 'to' date options > 'from' date
    return fromDateTime ? fromDateTime.toISOString() : undefined;
  }

  /**
   * Limit max possible 'from' date
   */
  getFromDateMax() {
    const toDateTime = this.getDateTime('to');
    // if there is a 'to' date selected, 'from' date options < 'to', otherwise set max to present
    return toDateTime ? toDateTime.toISOString() : this.now.toISOString();
  }

  /**
   * Limit max possible 'to' date
   */
  getToDateMax() {
    // to date cannot be in the future
    return this.now.toISOString();
  }

  /**
   * Determines diff between stored filter value and local form value.
   * used to determine if 'Apply' button should be enabled.
   * @param filterModel
   */
  isDifference(filterModel: { from: string; to: string }) {
    return !(filterModel?.from === this.getDateTime('from')?.toISOString() &&
        filterModel?.to === this.getDateTime('to')?.toISOString());
  }

  /**
   * Get a Date object consisting of both the datepicker and timepicker values.
   * @param param
   * @private
   */
  private getDateTime(param: 'from' | 'to'): Date {
    const date = this.filterState?.get(`${param}Date`)?.value;

    if (!date) {
      return null;
    }

    const time = this.filterState?.get(`${param}Time`)?.value;
    const dateTime = new Date(date.getTime()); // clone Date object

    if (!time) {
      dateTime.setHours(param === 'from' ? 0 : 23);
      dateTime.setMinutes(param === 'from' ? 0 : 59);
    } else {
      dateTime.setHours(time.getHours());
      dateTime.setMinutes(time.getMinutes());
    }

    return dateTime;
  }
}
